/**
 * Pairs Trading Risk Utilities
 * Helper functions for risk calculation and position sizing
 */

/**
 * Parse risk input from user (e.g., "2%", "$200", "200")
 * @param {string} input - User input for risk amount
 * @param {number} walletBalance - Current wallet balance in USD
 * @param {number} defaultPct - Default percentage if input is empty (default 0.5%)
 * @returns {number} Risk amount in USD
 */
function parseRiskInput(input, walletBalance, defaultPct = 0.5) {
    if (!input || input.trim() === '') {
        return walletBalance * (defaultPct / 100);
    }

    const trimmed = input.trim();

    // Percentage format: "2%" or "2.5%"
    if (trimmed.endsWith('%')) {
        const pct = parseFloat(trimmed.slice(0, -1));
        if (isNaN(pct) || pct <= 0 || pct > 100) {
            // console.warn('[PAIRS-RISK] Invalid percentage, using default');
            return walletBalance * (defaultPct / 100);
        }
        return walletBalance * (pct / 100);
    }

    // Dollar format: "$200" or "$200.50"
    if (trimmed.startsWith('$')) {
        const amount = parseFloat(trimmed.slice(1));
        if (isNaN(amount) || amount <= 0) {
            // console.warn('[PAIRS-RISK] Invalid dollar amount, using default');
            return walletBalance * (defaultPct / 100);
        }
        return Math.min(amount, walletBalance); // Cap at wallet balance
    }

    // Plain number (assume USD)
    const amount = parseFloat(trimmed);
    if (isNaN(amount) || amount <= 0) {
        // console.warn('[PAIRS-RISK] Invalid amount, using default');
        return walletBalance * (defaultPct / 100);
    }
    return Math.min(amount, walletBalance);
}

/**
 * Calculate leg sizes based on hedge ratio (from log-price Kalman)
 * @param {number} totalRiskUsd - Size per leg in USD
 * @param {number} hedgeRatio - Gamma from log-price Kalman filter (typically ~1.0)
 * @returns {{legA: number, legB: number}} Size for each leg in USD
 */
function calculateLegSizes(totalRiskUsd, hedgeRatio) {
    const absGamma = Math.abs(hedgeRatio || 1.0);

    // Each leg uses the full input size (not divided)
    const legA = totalRiskUsd;
    const legB = totalRiskUsd * absGamma;

    return { legA, legB };
}

/**
 * Calculate position sizes in asset units
 * @param {number} legUsd - Leg size in USD
 * @param {number} price - Current asset price
 * @returns {number} Position size in asset units
 */
function usdToAssetSize(legUsd, price) {
    if (!price || price <= 0) return 0;
    return legUsd / price;
}

// Export to window for V4 browser services
if (typeof window !== 'undefined') {
    window.PairsRiskUtils = {
        parseRiskInput,
        calculateLegSizes,
        usdToAssetSize
    };
}
